// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.GridPreviewStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import {
  flatMap,
  flow,
  reduce,
  concat,
  filter,
  map,
  get,
  getOr,
  take,
  find,
  findIndex
} from 'lodash/fp';
import { action, observable, toJS } from 'mobx';
import ReportPartGridContent from 'logic/models/Izenda.ReportPartGridContent';
import { GRID_STYLES, FREEZE_HEADERS } from 'logic/data/Izenda.ReportPartGridConstants';
import ReportPartDetails from 'logic/models/Izenda.ReportPartDetails';
import ReportPartGridProperties from 'logic/models/Izenda.ReportPartGridProperties';
import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import { ContainerName as ChartContainerName, isChartType } from 'logic/data/Izenda.ReportPartChartConstants';

export default class GridPreviewStore {
  gridReportParts = [];
  // Current metric selected in report part chart
  @observable selectedMetricIndex = -1;
  // Current grid to show in the grid preview in case there are multiple record set
  @observable showingPartIndex = 0;
  @observable isProcessingData = true;

  /**
   * Get ordering containers of report part content by mapping definition
   * @param {any} reportPartContent
   * @returns {Array<any>} Collection of containers.
   */
  getMappingContainers(reportPartContent: any): Array<any> {
    const isLabelsValuesGroup = isChartType(
      reportPartContent.chartType,
      CHART_TYPE.Line,
      CHART_TYPE.Column,
      CHART_TYPE.Bar,
      CHART_TYPE.Area,
      CHART_TYPE.Pie,
      CHART_TYPE.Funnel,
      CHART_TYPE.Donut,
      CHART_TYPE.TreeMap,
      CHART_TYPE.Combination,
      CHART_TYPE.Waterfall,
      CHART_TYPE.Sparkline
    );

    const isLabelsValuesValuesLabelsGroup = isChartType(
      reportPartContent.chartType,
      CHART_TYPE.HeatMap,
      CHART_TYPE.Scatter
    );

    const isLabelsValuesBubbleSizeValuesLabelGroup = isChartType(reportPartContent.chartType, CHART_TYPE.Bubble);

    const GAUGE_CHARTS = [CHART_TYPE.SimpleGauge, CHART_TYPE.LinearGauge, CHART_TYPE.SolidGauge];
    const baseChartType = reportPartContent.getBaseChartType && reportPartContent.getBaseChartType(reportPartContent.chartType) || '';
    const isGaugeGroup = isChartType(reportPartContent.chartType, ...GAUGE_CHARTS) || (baseChartType && isChartType(baseChartType, ...GAUGE_CHARTS));

    let mapping = [];

    switch (true) {
      case isLabelsValuesGroup:
        mapping = [ChartContainerName.LABELS, ChartContainerName.VALUES];
        break;
      case isLabelsValuesValuesLabelsGroup:
        mapping = [ChartContainerName.LABELS, ChartContainerName.VALUES, ChartContainerName.VALUES_LABELS];
        break;

      case isLabelsValuesBubbleSizeValuesLabelGroup:
        mapping = [
          ChartContainerName.LABELS,
          ChartContainerName.VALUES,
          ChartContainerName.BUBBLE_SIZE,
          ChartContainerName.VALUES_LABELS
        ];
        break;
      case isGaugeGroup:
        mapping = [ChartContainerName.LABELS, ChartContainerName.VALUES];
        break;
    }

    const containers = mapping.reduce((result, type) => concat(result, get(type, reportPartContent)), []);
    // DYNAMIC_THRESHOLD value will be gathered in another story.
    const selectedCustomContainers = filter(
      c => get('name', c) !== ChartContainerName.DYNAMIC_THRESHOLD,
      reportPartContent.customContainers
    );

    return [...containers, ...selectedCustomContainers];
  }

  /**
   * Build column collection
   * @param {any} reportPart
   * @returns {any} column detail
   */
  buildColumns(reportPart: any): any {
    const containers = this.getMappingContainers(reportPart.reportPartContent);
    const expandedLevel = getOr(0, 'reportPartContent.expandedLevel', reportPart);
    const metricContainerName = reportPart.reportPartContent.getMetricContainer();
    const activeSerie = reportPart.reportPartContent.activeSerie;

    return {
      elements: flow(
        flatMap(this.applyExpandedLevelAndMetric(expandedLevel, metricContainerName, activeSerie)),
        reduce((result, items) => concat(result, items), []),
        map(this.removeLinkedObject)
      )(containers)
    };
  }

  /**
   * Remove field which are not include in expand level or not the current metric
   * @param {number} expandedLevel
   * @param {string} metricContainerName
   * @param {any} activeSerie
   */
  applyExpandedLevelAndMetric(expandedLevel: number, metricContainerName: string, activeSerie: any): any {
    return (container: any) => {
      const { visibleElement, name } = container;
      if (name === ChartContainerName.LABELS && expandedLevel >= 0) {
        return take(++expandedLevel, visibleElement);
      } else if (name === metricContainerName && activeSerie) {
        return filter(ele => ele === activeSerie, visibleElement);
      }
      return visibleElement;
    };
  }

  /**
   * Build separator collection
   * @param {any} reportPart
   */
  buildSeparators(reportPart: any): any {
    return {
      elements: map(this.removeLinkedObject, get('separators.elements', reportPart.reportPartContent))
    };
  }

  /**
   * Remove linked object from field, to ensure no subreport, custom URL or embedded javascript showed
   * @param {any} element
   */
  removeLinkedObject(element: any): any {
    const newElement = toJS(element);
    if (element && element.properties) {
      const { properties } = newElement;
      properties.drillDown.subReport.selectedReport = null;
      properties.drillDown.subReport.reportPartUsed = null;
      properties.dataFormattings.customURL.url = '';
      properties.dataFormattings.embeddedJavascript.script = '';
    }
    return newElement;
  }

  /**
   * Build vertical grid detail
   * @param {any} reportPart
   * @param {any} queryData
   * @param {number} index
   * @param {Array<any>} metricContainer
   */
  buildVerticalGridDetail(reportPart: any, queryData: any, index: number, metricContainer: Array<any>): any {
    const columns = this.buildColumns(reportPart);
    const separators = this.buildSeparators(reportPart);
    const data = Object.assign({}, reportPart.reportPartContent, {
      type: GRID_STYLES.VERTICAL.value,
      columns,
      separators
    });

    data.properties = new ReportPartGridProperties();
    data.properties.grouping.useSeparator = true;
    data.properties.view = reportPart.reportPartContent.properties.view;
    data.properties.headers.freezeHeaders = FREEZE_HEADERS.VERTICALLY.value;

    const gridPart = new ReportPartDetails();
    gridPart.reportPartContent = new ReportPartGridContent(data);
    gridPart.queryData = queryData;

    return {
      partDefinition: gridPart,
      metric: { label: get('name', metricContainer.elements[index]), value: index }
    };
  }

  /**
   * Build all grid report parts
   * @param {any} reportPart
   */
  @action
  buildGridReportParts = (reportPart: any): void => {
    this.isProcessingData = true;
    const { queryData } = reportPart;
    const metricContainerName = reportPart.reportPartContent.getMetricContainer();
    const metricContainer = find(con => con.name === metricContainerName, reportPart.containerCollection);

    this.gridReportParts = queryData.map((data, index) =>
      this.buildVerticalGridDetail(reportPart, data, index, metricContainer)
    );

    let defaultIndex = -1;
    if (this.gridReportParts.length > 1) {
      const activeSerie = reportPart.reportPartContent.activeSerie;
      const queryIndex = findIndex(ele => ele === activeSerie, metricContainer.elements);
      this.selectedMetricIndex = queryIndex >= 0 ? queryIndex : defaultIndex;
    }

    return Promise.resolve(true);
  };

  /**
   * Update selected showing part index
   * @param {Number} value
   */
  updateShowingPartIndex(value: number) {
    this.showingPartIndex = value;
  }

  /**
   * Turn off processing data flag
   */
  turnOffProcessingDataIndicator() {
    this.isProcessingData = false;
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.GridPreviewStore.js